import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { OtpInput } from '../../components/ui/OtpInput';
import { api, ApiError } from '../../lib/api';
import { setTokens } from '../../lib/tokens';

type VerifyCodeModalProps = {
  open: boolean;
  onClose: () => void;
  email: string;
  challengeId: string;
};

const RESEND_SECONDS = 42;

export function VerifyCodeModal({ open, onClose, email, challengeId }: VerifyCodeModalProps) {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [activeChallengeId, setActiveChallengeId] = useState(challengeId);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCode('');
    setError(null);
    setActiveChallengeId(challengeId);
    setSecondsLeft(RESEND_SECONDS);
  }, [open, challengeId]);

  useEffect(() => {
    if (!open || secondsLeft <= 0) return;
    const timer = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [open, secondsLeft]);

  const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const seconds = String(secondsLeft % 60).padStart(2, '0');

  const handleConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const response = await api.verify({ challenge_id: activeChallengeId, code });
      setTokens(response.tokens.access_token, response.tokens.refresh_token);
      onClose();
      navigate('/workspace');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось подтвердить код.');
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    try {
      const response = await api.resendCode(activeChallengeId);
      setActiveChallengeId(response.challenge_id);
      setCode('');
      setSecondsLeft(RESEND_SECONDS);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось отправить код повторно.');
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className="flex flex-col items-center text-center">
        <div className="flex size-12 items-center justify-center rounded-[14px] bg-ai-accent text-xl text-text-inverse">
          ✦
        </div>
        <h2 className="mt-6 text-xl font-semibold text-text-primary">Введите код подтверждения</h2>
        <p className="mt-3 text-xs leading-relaxed text-text-secondary">
          Мы отправили шестизначный код на
          <br />
          {email}
        </p>
        <button type="button" onClick={onClose} className="mt-2 text-[11px] font-medium text-action-primary">
          Изменить e-mail
        </button>

        <div className="mt-6">
          <OtpInput value={code} onChange={setCode} />
        </div>

        {error && <p className="mt-3 text-xs font-medium text-status-warning">{error}</p>}

        <p className="mt-4 text-[11px] text-text-secondary">
          {secondsLeft > 0 ? (
            `Получить новый код через ${minutes}:${seconds}`
          ) : (
            <button type="button" onClick={handleResend} className="font-medium text-action-primary">
              Отправить код ещё раз
            </button>
          )}
        </p>

        <Button
          type="button"
          className="mt-4 w-full"
          disabled={code.length < 6 || submitting}
          onClick={handleConfirm}
        >
          {submitting ? 'Проверяем…' : 'Подтвердить'}
        </Button>
      </div>
    </Modal>
  );
}
